import { PAP_Sign_SignatureRequest } from '@dropbox/pap-events/sign/sign_signature_request';
import {
  AttachmentSlot,
  FieldJoined,
  QualifiedSignatureProperties,
  ResponseError,
  SignerAppReadyResponse,
} from '@dropbox/sign-internal-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ErrorWithContext } from 'common/utils/sentry/error-with-context';
import delay from 'hellospa/common/utils/delay';
import { LocationDescriptor } from 'history';
import invariant from 'invariant';
import { useAtomValue } from 'jotai';
import HfReactHelper from 'js/sign-components/common/hf-react-helper';
import { useLatestRef } from 'js/sign-components/common/use-latest-ref';
import React from 'react';
import { useHistory } from 'react-router';
import { useBundleProps } from 'signer-app/bundle-props';
import { useSignerAppClient } from 'signer-app/context/signer-app-client';
import { EsignDisclosureOptInArgs } from 'signer-app/context/signer-app-client/signer-api';
import {
  extractSavedData,
  populateFieldValues,
} from 'signer-app/signer-experience/saved-data-utils';
import { SignatureRequestResponse } from 'signer-app/signer-experience/types';
import { useTouchField } from 'signer-app/signer-experience/use-validation';
import { accessibleViewAtom } from 'signer-app/signer-signature-document/accessible-document-toggle';
import {
  FieldSave,
  SignatureRequest,
} from 'signer-app/signer-signature-document/types';
import { Field } from 'signer-app/types/editor-types';
import { useFeatureFlag } from 'js/sign-components/common/feature-flag';
import { getCSRFToken } from 'signer-app/utils/hs-fetch';
import { logPAPEvent } from 'signer-app/utils/log-pap-event';
import { redirectTo } from 'signer-app/utils/redirect';
import { useReady } from 'signer-app/utils/signer-app-ready';
import { unreachable } from 'signer-app/utils/unreachable';

export const typeMap: Record<Field['type'], FieldSave['componentType']> = {
  hyperlink: 'inputhyperlink',
  rectangle: 'inputrectangle',
  image: 'inputimage',
  date: 'inputdate',
  dropdown: 'inputdropdown',
  checkbox: 'inputcheckmark',
  radiobutton: 'inputradiobutton',
  initials: 'inputinitials',
  signature: 'inputsignature',
  text: 'inputtextbox',
};

const responseToSentryContext = async (response: Response) => {
  const responseBody = await response
    .clone()
    .json()
    .catch(() => response.text())
    .catch(() => 'Unable to read response body');

  return {
    status: response.status,
    statusText: response.statusText,
    url: response.url,
    headers: Object.fromEntries(response.headers.entries()),
    body: responseBody,
  };
};

function getPostsignUrl({
  type,
  signerCacheKey,
  isEmbedded,
}: {
  type: string;
  signerCacheKey: string;
  isEmbedded: boolean;
}) {
  const cacheKey = encodeURIComponent(signerCacheKey);

  const searchParams = new URLSearchParams();
  searchParams.set('email_key', cacheKey);
  searchParams.set('is_embedded', isEmbedded ? '1' : '0');

  return HfReactHelper.urlHelper(
    `postsign/${type}?${searchParams.toString()}`,
    HfReactHelper.HfSites.SITE_CODE_HS,
  );
}

/**
 * Returns a location description for the eID page, if applicable.
 */
function getEidRedirect(
  signatureRequestId: string,
  data: Readonly<Partial<QualifiedSignatureProperties>>,
): LocationDescriptor | null {
  const {
    qesUrl,
    eidProviderClientId,
    eidHasAdditionalSigningMethods,
    eidCountryCode,
  } = data;
  if (qesUrl && eidProviderClientId) {
    const searchParams = new URLSearchParams();

    searchParams.set('qesUrl', qesUrl);
    searchParams.set('clientId', eidProviderClientId);
    searchParams.set(
      'hasAdditionalSigningMethods',
      eidHasAdditionalSigningMethods ? '1' : '0',
    );
    if (eidCountryCode) {
      searchParams.set('countryCode', eidCountryCode);
    }

    const search = searchParams.toString();
    return {
      pathname: `/sign/${signatureRequestId}/eidComplete`,
      search,
    };
  } else {
    return null;
  }
}

export const signExperienceKeys = {
  signerExperience: (signatureRequestId: string, accessCode: string) =>
    ['signer-experience', signatureRequestId, accessCode] as const,
};

export type AccessCodeResponse =
  | 'need-access-code'
  | 'invalid-access-code'
  | 'too-many-retries'
  | 'need-sms-auth'
  | 'sms-rate-limit';

export type BackendErrorType = 'authentication-error' | 'generic-error';

export type AutoSaveReturn = {
  isFetching: boolean;
  data:
    | Omit<SignatureRequestResponse, 'signatureRequest'>
    | undefined
    | AccessCodeResponse;
  smsMeta?: any;
  clearAuth: () => void;
  /**
   * This is the working copy of the signature request. It is optimistically
   * updated through `onFieldUpdate`
   */
  signatureRequest: SignatureRequest | undefined;

  onFieldUpdate?: (field: Field, updates: Partial<Field>) => void;
  uploadAttachment: (args: { attachmentSlotId: string; file: File }) => void;
  accessCode: string | null;
  setAccessCode: (accessCode: string | null) => void;
  hasSetAccessCode: boolean;
  esignDisclosureOptIn: (args: EsignDisclosureOptInArgs) => void;
  finalize: () => void;
  isFinalizing: boolean;
  setAuthState: (state: AccessCodeResponse | null) => void;
  setSmsMeta: (meta: any | null) => void;
  numAttemptsLeft: number;
  setNumAttemptsLeft: (num: number) => void;
  redirectUrl: string | undefined;
  backendError: BackendErrorType | undefined;
};

export const SAVE_DELAY = 2000;
export function useAutoSaveSignerExperience(
  signatureRequestId: string,
): AutoSaveReturn {
  const client = useSignerAppClient();
  const queryClient = useQueryClient();
  const [accessCode, setAccessCode] = React.useState<string | null>(null);
  const previousAccessCodeRef = React.useRef<string | null>(null);
  const history = useHistory();
  const touchField = useTouchField();
  const { signerAccountId, signerDbxAccountId, tsmGuid } = useBundleProps();
  const isFormViewToggleActive = useAtomValue(accessibleViewAtom);
  const preserveOriginalRequired = useFeatureFlag(
    'sign_core_2026_02_19_fix_conditional_logic_original_required',
  );
  const [redirectUrl, setRedirectUrl] = React.useState<string | undefined>();
  const [backendError, setBackendError] = React.useState<BackendErrorType>();
  const signerReadyBackoffTries = React.useRef({
    tries: 0,
    next: 0,
  });

  // Holds auth challenge state returned from /ready
  const [authState, setAuthState] = React.useState<AccessCodeResponse | null>(
    null,
  );

  const [numAttemptsLeft, setNumAttemptsLeft] = React.useState<number>(0);

  const [smsMeta, setSmsMeta] = React.useState<any | null>(null);

  const clearAuth = () => {
    setAuthState(null);
    setSmsMeta(null);
    setAccessCode(null);
  };

  const ready = useReady();

  const {
    data: readyData,
    isFetching: readyIsFetching,
    refetch: refetchReady,
  } = useQuery({
    queryKey: ['signer-experience', 'ready', tsmGuid, accessCode] as const,
    queryFn: () => {
      const codeToSend = accessCode ?? undefined;
      return ready({ tsmGuid, accessCode: codeToSend });
    },
    onSuccess(readyResponseData) {
      const code = readyResponseData?.error?.code;

      // Always refresh meta if present
      if (
        (code === 'sms_rate_limit' || code === 'sms_auth_required') &&
        (readyResponseData as any)?.error?.data
      ) {
        setSmsMeta((readyResponseData as any).error?.data);
      }
      const newAttemptsLeft =
        (readyResponseData as SignerAppReadyResponse).error?.data
          ?.numAttemptsLeft ?? 0;
      setNumAttemptsLeft(newAttemptsLeft);

      if (code === 'sms_rate_limit') {
        setAuthState('sms-rate-limit');
        return;
      }

      // Functional update to avoid stale closure
      setAuthState((prev) => {
        if (prev === 'sms-rate-limit') {
          return prev; // do not override rate-limit
        } else if (code === undefined) {
          return null;
        } else if (code === 'sms_auth_required') {
          return 'need-sms-auth';
        } else if (code === 'access_code_required') {
          return accessCode !== null
            ? 'invalid-access-code'
            : 'need-access-code';
        } else if (
          code === 'access_code_blocked' ||
          code === 'too_many_requests'
        ) {
          return 'too-many-retries';
        } else if (code === 'need_signer_choice') {
          // This case is intentionally unused in this flow
          return prev;
        } else {
          /**
           * By using an exhaustive type, we can make sure that there aren't any
           * scenarios that simply aren't being handled.
           */
          unreachable(code);
          return prev;
        }
      });
    },
    refetchInterval(data) {
      // Stop polling when any auth challenge active
      if (authState || data?.error?.code) return false;
      if (data?.isInFlightEditing) {
        redirectTo('/info/externalCantSignNow');
      }
      if (!data?.ready) {
        const now = Date.now();
        signerReadyBackoffTries.current.tries += 1;
        if (now >= signerReadyBackoffTries.current.next) {
          signerReadyBackoffTries.current.next =
            now + 5000 * signerReadyBackoffTries.current.tries;
        }

        const readyBackoff = signerReadyBackoffTries.current.next - now;
        return readyBackoff;
      }
      return false;
    },
  });

  const queryKey = [
    'signer-experience',
    signatureRequestId,
    accessCode,
  ] as const;

  const { data, isFetching } = useQuery({
    queryKey,
    enabled: !!readyData?.ready,
    useErrorBoundary() {
      return true;
    },
    keepPreviousData: true,
    queryFn: async () => {
      try {
        const response =
          await client.signerApi.signerAppSignatureRequestGetRequest(
            signatureRequestId,
            accessCode ?? undefined,
            tsmGuid ?? undefined,
          );

        const eidRedirect = getEidRedirect(
          signatureRequestId,
          response.qualifiedSignatureProperties ?? {},
        );
        if (eidRedirect) {
          history.push(eidRedirect);

          return response;
        }

        invariant(
          response.signatureRequest.pages.length > 0,
          'No pages found - invalid signature request',
        );
        invariant(
          response.signatureRequest.fields.length > 0,
          'No pages found - invalid signature request',
        );

        return {
          ...response,
          signatureRequest: {
            ...response.signatureRequest,
            fields: populateFieldValues(response.signatureRequest.fields, {
              defaultDateFormat: response.defaultDateFormat,
            }),
          },
        };
      } catch (error) {
        if ((error as Error).name === 'ResponseError') {
          const response = (error as ResponseError).response;
          const status = response.status;

          switch (status) {
            case 403:
              if (!accessCode) {
                return 'need-access-code';
              }
              return 'invalid-access-code';
            case 429:
              return 'too-many-retries';
            case 410:
              await redirectTo('/info/externalNothingToSign');
              break;
            case 401:
              await redirectTo(
                `/account/logIn?on_login_redirect_url=${encodeURIComponent(location.href)}`,
              );
              break;
            default: {
              throw new ErrorWithContext(
                `Unexpected ResponseError HTTP ${status}`,
                {
                  tags: {
                    errorType: 'signerAppGetFailure',
                    httpStatus: status,
                  },
                  contexts: {
                    response: await responseToSentryContext(response),
                    request: {
                      signatureRequestId,
                      tsmGuid,
                      hasAccessCode: !!accessCode,
                    },
                  },
                },
              );
            }
          }
        }
        // re-throw other errors to be caught by the error boundary (`useErrorBoundary: true`)
        throw error;
      }
    },
  });

  const signatureRequest =
    typeof data === 'string' ? undefined : data?.signatureRequest;

  // Override data to signal UI when sms auth is required
  const effectiveData = React.useMemo(() => {
    if (authState) {
      return authState;
    }
    return data;
  }, [authState, data]);

  const { mutate: uploadAttachment } = useMutation<
    unknown,
    unknown,
    {
      attachmentSlotId: string;
      file: File;
    }
  >({
    mutationKey: [signatureRequestId, accessCode, 'upload-attachment'] as const,
    mutationFn: async ({ attachmentSlotId, file }) => {
      const fileName = file.name;
      await client.signerApi.uploadAttachment(attachmentSlotId, {
        file,
        csrfToken: getCSRFToken(),
      });
      const uploadedAt = String(Date.now());
      queryClient.setQueryData(
        queryKey,
        (
          prev: SignatureRequestResponse | undefined,
        ): SignatureRequestResponse | undefined => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            signatureRequest: {
              ...prev.signatureRequest,
              attachmentSlots: prev.signatureRequest.attachmentSlots.map(
                (slot): AttachmentSlot => {
                  if (slot.id === attachmentSlotId) {
                    return {
                      ...slot,
                      uploadedAt,
                      fileName,
                    };
                  }
                  return slot;
                },
              ),
            },
          };
        },
      );
    },
  });

  const fieldMutationKey = [signatureRequestId, accessCode, 'fields'] as const;
  const { mutate: mutateFieldData, isLoading: isSavingFields } = useMutation<
    unknown,
    unknown,
    Record<Field['id'], Field>
  >({
    mutationKey: fieldMutationKey,
    mutationFn: async (updates) => {
      await queryClient.cancelQueries(fieldMutationKey);
      const data = queryClient.getQueryData<SignatureRequestResponse>(queryKey);
      if (data) {
        const fields = data.signatureRequest.fields.map(
          (f: FieldJoined): SignatureRequest['fields'][number] => {
            const saved = updates[f.id];
            return saved ?? f;
          },
        );
        queryClient.setQueryData(queryKey, {
          ...data,
          signatureRequest: {
            ...data.signatureRequest,
            fields,
          },
        });
        const savedData = fields.map((f) => [
          f.id,
          extractSavedData(f, { preserveOriginalRequired }),
        ]);
        await client.signerApi.signerAppSaveActionRequest({
          signatureRequestId,
          savedData: Object.fromEntries(savedData),
        });
        setDraft((prev) => {
          if (!prev) {
            return null;
          }

          const newDraft = { ...prev };
          Object.keys(updates).forEach((key) => {
            if (updates[key] === prev[key]) {
              delete newDraft[key];
            }
          });

          return Object.keys(newDraft).length > 0 ? newDraft : null;
        });
      }
    },
    onError: (error) => {
      const isResponseError = (error as ResponseError)?.response != null;
      if (isResponseError) {
        const status = (error as ResponseError).response.status;

        setBackendError(
          status === 401 || status === 403
            ? 'authentication-error'
            : 'generic-error',
        );
      } else {
        setBackendError('generic-error');
      }
    },
  });

  const [draft, setDraft] = React.useState<Record<Field['id'], Field> | null>(
    null,
  );
  React.useEffect(() => {
    if (draft) {
      const timeout = setTimeout(() => {
        mutateFieldData(draft);
      }, SAVE_DELAY);
      return () => clearTimeout(timeout);
    }
    return () => {};
  }, [draft, mutateFieldData]);

  const onFieldUpdate = React.useCallback(
    <T extends Field>(field: T, updates: Partial<T>): void => {
      const updatedField: Field = { ...field, ...updates };
      touchField(field.id);
      setDraft((prev) => {
        return {
          ...prev,
          [field.id]: updatedField,
        };
      });
    },
    [touchField],
  );

  const fields = React.useMemo((): SignatureRequest['fields'] => {
    const baseFields = signatureRequest?.fields;
    if (draft && baseFields) {
      return baseFields.map((field): FieldJoined => {
        const saved = draft[field.id];
        return saved ?? field;
      });
    }

    return baseFields ?? [];
  }, [draft, signatureRequest?.fields]);

  const { mutate: esignDisclosureOptIn } = useMutation({
    useErrorBoundary: true,
    mutationKey: [...queryKey, 'esign-opt-in'],
    mutationFn: async (args: EsignDisclosureOptInArgs) => {
      await client.signerApi.esignDisclosureOptIn(args);
    },
    onMutate: async () => {
      await queryClient.cancelQueries(queryKey);
      const prevResponse =
        queryClient.getQueryData<SignatureRequestResponse>(queryKey);

      queryClient.setQueryData<SignatureRequestResponse>(queryKey, (prev) => {
        if (prev) {
          return {
            ...prev,
            esignDisclosure: prev.esignDisclosure
              ? {
                  ...prev.esignDisclosure,
                  userOptIn: true,
                }
              : undefined,
          };
        }
        return undefined;
      });

      return { prevResponse };
    },
    onError: (_err, _args, context) => {
      queryClient.setQueryData(queryKey, context?.prevResponse);
    },
  });

  const isSavingRef = useLatestRef(isSavingFields);
  const draftRef = useLatestRef(draft);

  const { mutate: finalize, isLoading: isFinalizing } = useMutation({
    useErrorBoundary: true,
    mutationKey: [...queryKey, 'finalize'],
    mutationFn: async () => {
      // Wait until the draft is cleared and the mutation is not saving
      while (draftRef.current != null || isSavingRef.current) {
        // eslint-disable-next-line no-await-in-loop
        await delay(500);
      }

      const queryData =
        queryClient.getQueryData<SignatureRequestResponse>(queryKey);

      invariant(queryData, 'Cannot finalize without query data');

      // Don't allow finalizing if we're actively saving field data so
      // we get the latest data
      invariant(!isSavingFields, 'Cannot finalize while saving fields');

      const data = Object.fromEntries(
        queryData.signatureRequest.fields.map((field) => [
          field.id,
          extractSavedData(field, { preserveOriginalRequired }),
        ]),
      );

      // TODO: This error handling pattern is horrible and needs a refactor.
      // We have to catch errors here instead of in onError because React Query
      // doesn't propagate errors thrown from onError to the error boundary.
      // Ideally we should either:
      // 1. Remove useErrorBoundary and handle errors locally, or
      // 2. Use a proper error state management pattern instead of relying on error boundaries
      try {
        return await client.signerApi.signerAppFinalizeAction({
          guid: tsmGuid,
          accessCode: accessCode ?? undefined,
          data,
          isSmsDelivery: queryData.isComingFromSmsDelivery ?? false,
        });
      } catch (error) {
        if ((error as Error).name === 'ResponseError') {
          const response = (error as ResponseError).response;
          const status = response.status;

          if (status === 410) {
            await redirectTo('/info/externalNothingToSign');
            return;
          } else if (status === 401) {
            await redirectTo(
              `/account/logIn?on_login_redirect_url=${encodeURIComponent(location.href)}`,
            );
            return;
          } else {
            throw new ErrorWithContext(
              `Unexpected ResponseError HTTP ${status}`,
              {
                tags: {
                  errorType: 'signerAppFinalizeFailure',
                  httpStatus: status,
                },
                contexts: {
                  response: await responseToSentryContext(response),
                  request: {
                    signatureRequestId,
                    tsmGuid,
                    hasAccessCode: !!accessCode,
                  },
                },
              },
            );
          }
        }
        throw error;
      }
    },
    onSuccess: (response) => {
      if (!response) {
        // 401 and 410 errors already redirected, no need to process
        return;
      }

      const queryData =
        queryClient.getQueryData<SignatureRequestResponse>(queryKey);

      logPAPEvent(
        PAP_Sign_SignatureRequest({
          signatureRequestId,
          enableFormView: queryData?.isMobileReady,
          formViewToggle: queryData?.isMobileReady
            ? isFormViewToggleActive
            : undefined,
          signatureFrame:
            queryData?.signatureRequest?.signerOptions?.signatureFrame ?? false,
        }),
        signerDbxAccountId,
        signerAccountId,
      );

      const eidRedirect = getEidRedirect(signatureRequestId, response);

      if (eidRedirect) {
        history.push(eidRedirect);
      } else if (response.postsignPageType && response.cacheKey) {
        redirectTo(
          getPostsignUrl({
            type: response.postsignPageType,
            isEmbedded: queryData?.isEmbedded ?? false,
            signerCacheKey: response.cacheKey,
          }),
        );
      } else if (
        response.writeBack &&
        response.redirectUrl &&
        response.isTrusted &&
        (HfReactHelper.matchesHsDomain(response.redirectUrl, '/') ||
          HfReactHelper.matchesHsDomain(response.redirectUrl, '/home/index'))
      ) {
        redirectTo(`${response.redirectUrl}?writeBackSuccess=true`);
      } else {
        if (response.redirectUrl) {
          setRedirectUrl(response.redirectUrl);
        }
        history.push(`/sign/${signatureRequestId}/complete/`);
      }
    },
  });

  const handleAccessCodeSubmit = React.useCallback(
    (code: string | null) => {
      const isSameCode = code === previousAccessCodeRef.current;
      previousAccessCodeRef.current = code;
      setAccessCode(code);

      // same access code resubmitted
      // forces a refetch to retry the backend call
      // This ensures the retry count is updated and we get a fresh response
      if (isSameCode && code !== null) {
        refetchReady();
      }
    },
    [refetchReady],
  );

  return {
    onFieldUpdate,
    isFetching: readyIsFetching || isFetching,
    data: effectiveData as any,
    accessCode,
    setAccessCode: handleAccessCodeSubmit,
    hasSetAccessCode: accessCode !== null,
    uploadAttachment,
    esignDisclosureOptIn,
    finalize,
    isFinalizing,
    signatureRequest:
      signatureRequest == null
        ? undefined
        : ({
            ...signatureRequest,
            fields,
          } as typeof signatureRequest),
    smsMeta,
    clearAuth,
    setAuthState,
    setSmsMeta,
    numAttemptsLeft,
    setNumAttemptsLeft,
    redirectUrl,
    backendError,
  };
}
